/**
 * 制作工作台 · 道具设定图 Dialog
 * - 每张图是正/侧/背三视图合成图（单张出图，不分视角）
 * - 支持多形象(looks)，每个形象独立锁定
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ImageOff, Loader2, Pencil, Plus, Save, Sparkles, Trash2, Wand2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { Lightbox } from '@/pages/brain/Lightbox';
import type {
  DirectorPlan,
  DirectorProp,
  PropLook,
} from './types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  episodeId: string;
  prop: DirectorProp;
}

type Resolution = '2K' | '4K';
type Count = 1 | 2 | 3;

export function PropLockDialog({ open, onOpenChange, episodeId, prop: propData }: Props) {
  const qc = useQueryClient();
  const basePath = `director/episodes/${episodeId}/director/props/${propData.id}`;

  const planQuery = useQuery<DirectorPlan>({
    queryKey: ['director', 'plan', episodeId],
    queryFn: () => api.get(`director/episodes/${episodeId}/director/plan`).json<DirectorPlan>(),
    enabled: open,
    staleTime: 0,
    refetchInterval: (q) => {
      const data = q.state.data as DirectorPlan | undefined;
      if (!data) return false;
      const p = (data.props ?? []).find((pp) => pp.id === propData.id);
      if (!p) return false;
      const allRuns = [
        ...(p.lockRuns ?? []),
        ...(p.looks ?? []).flatMap((lk) => lk.lockRuns ?? []),
      ];
      const anyRunning = allRuns.some((r) => {
        if (r.status !== 'running' && r.status !== 'queued') return false;
        return !(r.candidates.length > 0 && r.candidates.every((c) => c.url || c.error));
      });
      return anyRunning ? 3000 : false;
    },
  });

  const prop = useMemo(
    () => (planQuery.data?.props ?? []).find((p) => p.id === propData.id) ?? propData,
    [planQuery.data, propData],
  );

  const [activeLookId, setActiveLookId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<Resolution>('2K');
  const [count, setCount] = useState<Count>(3);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const [showNewLookForm, setShowNewLookForm] = useState(false);
  const [newLookDesc, setNewLookDesc] = useState('');
  const [editingLookId, setEditingLookId] = useState<string | null>(null);
  const [editLookDesc, setEditLookDesc] = useState('');

  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesHint, setNotesHint] = useState('');

  const container = activeLookId
    ? (prop.looks ?? []).find((l) => l.id === activeLookId) ?? prop
    : prop;
  const lockedUrl: string | null | undefined = activeLookId
    ? (container as PropLook).lockedImageUrl
    : prop.lockedImageUrl;
  const containerLabel = activeLookId ? ((container as PropLook).label ?? '新形象') : '';
  const containerNotes = (container as { notes?: string | null }).notes ?? '';
  const lockRuns = container.lockRuns ?? [];

  const generateMut = useMutation({
    mutationFn: () =>
      api
        .post(`${basePath}/lock/generate`, {
          json: { resolution, count, lookId: activeLookId || undefined },
        })
        .json<{ taskId: string }>(),
    onSuccess: () => {
      toast.success('已开始生成');
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    },
    onError: (e: Error) => toast.error('生成失败', e.message || '请重试'),
  });

  const selectMut = useMutation({
    mutationFn: (candidateId: string) =>
      api
        .post(`${basePath}/lock/select`, {
          json: { candidateId, lookId: activeLookId || undefined },
        })
        .json(),
    onSuccess: () => {
      toast.success('已锁定');
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    },
    onError: (e: Error) => toast.error('锁定失败', e.message || '请重试'),
  });

  const deleteLockMut = useMutation({
    mutationFn: () =>
      api.delete(`${basePath}/lock`, {
        searchParams: activeLookId ? { look_id: activeLookId } : undefined,
      }).json(),
    onSuccess: () => {
      toast.success('已删除');
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const createLookMut = useMutation({
    mutationFn: () =>
      api.post(`${basePath}/looks`, { json: { description: newLookDesc.trim() } }).json<PropLook>(),
    onSuccess: (lk) => {
      setShowNewLookForm(false);
      setNewLookDesc('');
      setActiveLookId(lk.id);
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('新形象已创建');
    },
    onError: (e: Error) => toast.error('创建失败', e.message || '请重试'),
  });

  const updateLookMut = useMutation({
    mutationFn: () =>
      api.patch(`${basePath}/looks/${editingLookId}`, { json: { description: editLookDesc.trim() } }).json(),
    onSuccess: () => {
      setEditingLookId(null);
      setEditLookDesc('');
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('形象已更新');
    },
    onError: (e: Error) => toast.error('更新失败', e.message || '请重试'),
  });

  const deleteLookMut = useMutation({
    mutationFn: (lookId: string) => api.delete(`${basePath}/looks/${lookId}`).json(),
    onSuccess: () => {
      setActiveLookId(null);
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('形象已删除');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const generateNotesMut = useMutation({
    mutationFn: () => {
      const path = activeLookId
        ? `${basePath}/looks/${activeLookId}/generate-notes`
        : `${basePath}/generate-notes`;
      return api.post(path, { json: { user_hint: notesHint.trim() || null } }).json<{ notes: string }>();
    },
    onSuccess: (data) => {
      setNotesDraft(data.notes);
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('AI 描述已生成');
    },
    onError: (e: Error) => toast.error('AI 生成失败', e.message || '请重试'),
  });

  const saveNotesMut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { notes: notesDraft.trim() || null };
      if (activeLookId) {
        return api.patch(`${basePath}/looks/${activeLookId}`, { json: body }).json();
      }
      return api.patch(basePath, { json: body }).json();
    },
    onSuccess: () => {
      setNotesEditing(false);
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('描述已保存');
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => { if (!o) onOpenChange(false); }}
        title={`道具设定 · ${prop.name}`}
        description="三视图合成图(正/侧/背同框) · 每个形象独立锁定"
        className="max-w-3xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>关闭</Button>
            <Button
              variant="agent"
              agentColor="director"
              onClick={() => generateMut.mutate()}
              disabled={generateMut.isPending}
            >
              <Wand2 className="h-3.5 w-3.5" />
              {generateMut.isPending ? '提交中…' : '生成三视图'}
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-2">
            <div>
              <div className="mb-1.5 text-xs font-medium text-neutral-700">形象变体</div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveLookId(null)}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] transition ${
                    !activeLookId
                      ? 'border-brand-500 bg-brand-500/10 text-brand-600'
                      : 'border-outline-variant text-neutral-500 hover:border-outline'
                  }`}
                >基础</button>
                {(prop.looks ?? []).map((lk) => (
                  <button
                    key={lk.id}
                    type="button"
                    onClick={() => setActiveLookId(lk.id)}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] transition ${
                      activeLookId === lk.id
                        ? 'border-brand-500 bg-brand-500/10 text-brand-600'
                        : 'border-outline-variant text-neutral-500 hover:border-outline'
                    }`}
                  >{lk.label}</button>
                ))}
                {showNewLookForm ? (
                  <div className="flex items-center gap-1">
                    <input autoFocus value={newLookDesc} onChange={(e) => setNewLookDesc(e.target.value)}
                      placeholder="形象描述"
                      onKeyDown={(e) => { if (e.key === 'Enter' && newLookDesc.trim()) createLookMut.mutate(); if (e.key === 'Escape') { setShowNewLookForm(false); setNewLookDesc(''); } }}
                      className="w-24 rounded border border-outline-variant/40 bg-white px-1.5 py-0.5 text-[10px] focus:border-brand-400 focus:outline-none" />
                    <button type="button" disabled={!newLookDesc.trim() || createLookMut.isPending}
                      onClick={() => createLookMut.mutate()} className="rounded text-brand-500 hover:text-brand-600"><Check className="h-3 w-3" /></button>
                    <button type="button" onClick={() => { setShowNewLookForm(false); setNewLookDesc(''); }}
                      className="rounded text-neutral-400 hover:text-neutral-600"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setShowNewLookForm(true)}
                    className="rounded-full border border-dashed border-outline-variant/50 px-2 py-0.5 text-[11px] text-neutral-400 hover:border-brand-400 hover:text-brand-500">
                    <Plus className="inline h-3 w-3" /> 新形象
                  </button>
                )}
              </div>
              {activeLookId && (() => {
                const lk = (prop.looks ?? []).find((l) => l.id === activeLookId);
                if (!lk) return null;
                return editingLookId === lk.id ? (
                  <div className="mt-1.5 flex items-center gap-1">
                    <input autoFocus value={editLookDesc} onChange={(e) => setEditLookDesc(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') updateLookMut.mutate(); if (e.key === 'Escape') { setEditingLookId(null); setEditLookDesc(''); } }}
                      className="flex-1 rounded border border-outline-variant/40 bg-white px-1.5 py-0.5 text-[10px]" />
                    <button type="button" onClick={() => updateLookMut.mutate()} className="text-brand-500"><Save className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="mt-1.5 flex items-center gap-1 text-[10px] text-neutral-500">
                    <span>{lk.description || lk.label}</span>
                    <button type="button" onClick={() => { setEditLookDesc(lk.description || ''); setEditingLookId(lk.id); }}><Pencil className="h-2.5 w-2.5" /></button>
                    <button type="button" onClick={() => deleteLookMut.mutate(lk.id)} className="text-red-400"><Trash2 className="h-2.5 w-2.5" /></button>
                  </div>
                );
              })()}
            </div>

            <div className="flex items-center gap-3">
              <div>
                <div className="mb-1 text-[10px] text-neutral-500">质量</div>
                <div className="flex gap-1">
                  {(['2K', '4K'] as Resolution[]).map((r) => (
                    <button key={r} type="button" onClick={() => setResolution(r)}
                      className={`rounded border px-2 py-0.5 text-[10px] transition ${
                        resolution === r ? 'border-brand-500 bg-brand-500/10 text-brand-600' : 'border-outline-variant text-neutral-500'}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[10px] text-neutral-500">张数</div>
                <div className="flex gap-1">
                  {([1, 2, 3] as Count[]).map((c) => (
                    <button key={c} type="button" onClick={() => setCount(c)}
                      className={`rounded border px-2 py-0.5 text-[10px] transition ${
                        count === c ? 'border-brand-500 bg-brand-500/10 text-brand-600' : 'border-outline-variant text-neutral-500'}`}>{c}</button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-1.5 text-xs font-medium text-neutral-700">
                锁定图{containerLabel ? ` · ${containerLabel}` : ''}
              </div>
              {lockedUrl ? (
                <div className="relative">
                  <button type="button" className="block w-full overflow-hidden rounded-md border border-outline-variant"
                    onClick={() => setLightboxUrl(lockedUrl)}>
                    <img src={lockedUrl} alt="三视图" className="w-full object-contain" />
                  </button>
                  <button type="button" onClick={() => deleteLockMut.mutate()}
                    className="absolute right-1 top-1 rounded bg-black/50 p-1 text-white hover:bg-red-500">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex aspect-[4/3] items-center justify-center rounded-md border border-dashed border-outline-variant/40 bg-surface-variant/20 text-neutral-400">
                  <ImageOff className="h-6 w-6" />
                </div>
              )}
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-neutral-700">
                <span>描述说明</span>
                {!notesEditing && (
                  <button
                    type="button"
                    onClick={() => { setNotesDraft(containerNotes); setNotesHint(''); setNotesEditing(true); }}
                    className="text-neutral-400 hover:text-brand-500"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
              {notesEditing ? (
                <div className="space-y-2">
                  <textarea
                    autoFocus
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={4}
                    placeholder="描述道具的造型、材质、色彩、细节，辅助 AI 生成更精确的三视图..."
                    className="w-full resize-none rounded border border-outline-variant/40 bg-surface px-2 py-1.5 text-[11px] leading-relaxed focus:border-brand-400 focus:outline-none"
                  />
                  <div className="flex items-center gap-1.5">
                    <input
                      value={notesHint}
                      onChange={(e) => setNotesHint(e.target.value)}
                      placeholder="AI 生成提示（可选，如：增加锈蚀痕迹）"
                      className="flex-1 rounded border border-outline-variant/40 bg-surface px-1.5 py-0.5 text-[10px] focus:border-brand-400 focus:outline-none"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={generateNotesMut.isPending}
                      onClick={() => generateNotesMut.mutate()}
                      className="gap-1 text-[10px]"
                    >
                      <Wand2 className="h-3 w-3" />
                      {generateNotesMut.isPending ? '生成中…' : 'AI 生成'}
                    </Button>
                  </div>
                  <div className="flex justify-end gap-1.5">
                    <Button variant="ghost" size="sm" onClick={() => setNotesEditing(false)}>取消</Button>
                    <Button
                      variant="agent"
                      agentColor="director"
                      size="sm"
                      disabled={saveNotesMut.isPending}
                      onClick={() => saveNotesMut.mutate()}
                    >
                      <Save className="h-3 w-3" /> 保存
                    </Button>
                  </div>
                </div>
              ) : containerNotes ? (
                <p className="text-[11px] leading-relaxed text-neutral-600 whitespace-pre-wrap">
                  {containerNotes}
                </p>
              ) : (
                <p className="text-[11px] text-neutral-400 italic">暂无描述。点击编辑或使用 AI 生成辅助说明。</p>
              )}
            </div>
          </div>

          <div className="space-y-3 lg:col-span-3">
            <div className="text-xs font-medium text-neutral-700">抽卡历史</div>
            {lockRuns.length === 0 ? (
              <p className="text-[11px] text-neutral-400">暂无生成记录</p>
            ) : (
              <div className="max-h-[60vh] space-y-3 overflow-y-auto">
                {[...lockRuns].reverse().map((run) => (
                  <div key={run.id} className="rounded-md border border-outline-variant/40 bg-surface-variant/10 p-2">
                    <div className="mb-1.5 flex items-center justify-between text-[10px] text-neutral-500">
                      <span>
                        {run.status === 'running' || run.status === 'queued' ? (
                          <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />生成中 {run.candidates.filter((c) => c.url || c.error).length}/{run.count}</span>
                        ) : run.status === 'succeeded' ? (
                          <span className="text-green-600">已完成 {run.candidates.filter((c) => c.url).length} 张</span>
                        ) : run.status === 'partial' ? (
                          <span className="text-amber-600">部分完成</span>
                        ) : (
                          <span className="text-red-500">失败</span>
                        )}
                      </span>
                      <span>{new Date(run.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {Array.from({ length: run.count ?? 0 }).map((_, i) => {
                        const cand = run.candidates[i];
                        if (!cand) {
                          return <div key={i} className="flex aspect-square animate-pulse items-center justify-center rounded bg-surface-variant/40 text-outline"><Sparkles className="h-4 w-4" /></div>;
                        }
                        if (cand.error) {
                          return <div key={cand.id} className="flex aspect-square items-center justify-center rounded bg-red-50 p-1 text-center text-[9px] text-red-500">{cand.error.slice(0, 30)}</div>;
                        }
                        const isLocked = lockedUrl === cand.url;
                        const imgUrl = cand.url ?? '';
                        return (
                          <div key={cand.id} className="group relative aspect-square overflow-hidden rounded border border-outline-variant/20">
                            <button type="button" className="absolute inset-0" onClick={() => setLightboxUrl(imgUrl)}>
                              <img src={imgUrl} alt="" className="h-full w-full object-cover" />
                            </button>
                            {isLocked && (
                              <div className="absolute left-0.5 top-0.5 rounded-full bg-brand-500 p-0.5 text-white"><Check className="h-2 w-2" /></div>
                            )}
                            {!isLocked && (run.status === 'succeeded' || run.status === 'partial') && (
                              <button type="button" onClick={() => selectMut.mutate(cand.id)}
                                className="absolute bottom-0 left-0 right-0 bg-black/60 py-0.5 text-center text-[9px] text-white opacity-0 transition group-hover:opacity-100">
                                {selectMut.isPending ? '锁定中…' : '锁定为设定图'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
      <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </>
  );
}
